import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowRight, ShieldCheck, Info, Shield, FileSignature, ChevronRight, Bell } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { API_CONFIG, setAuthToken } from '../config/api';
import { getPlatform } from '../config/platform';
import { clearStoredDriverMobileUser, setStoredDriverMobileUser } from '../utils/driverMobileSession';
import { isAnyDriverUser, isInternalDriverUser } from '../utils/userRoles';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';
import { alwaysLocationService } from '../services/alwaysLocationService';
import logo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';

export function DriverMobileAuth() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'phone' | 'verification'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [driverCategory, setDriverCategory] = useState<'özmal' | 'spot' | 'yeni-kayit' | ''>('');
  const [challengeToken, setChallengeToken] = useState('');

  const fetchSmsChallenge = useCallback(async () => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/auth/sms-challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data?.data?.challengeToken) {
        setChallengeToken(data.data.challengeToken);
        return data.data.challengeToken as string;
      }
    } catch (err) {
      console.error('SMS challenge fetch error:', err);
    }
    return '';
  }, []);

  useEffect(() => {
    void fetchSmsChallenge();
  }, [fetchSmsChallenge]);

  // Telefon numarası formatlama - sadece rakam, 10 hane
  const handlePhoneChange = (value: string) => {
    const cleaned = value.replace(/\D/g, ''); // Sadece rakamlar
    if (cleaned.length <= 10) {
      setPhoneNumber(cleaned);
      setError('');
    }
  };

  // SMS Gönder (Mock - Gerçek sistemde SMS API'si kullanılacak)
  const sendVerificationCode = async () => {
  setError("");

  if (phoneNumber.length !== 10) {
    setError("Telefon numarası 10 haneli olmalı");
    return;
  }

  setLoading(true);

  try {
    const freshToken = await fetchSmsChallenge();

    const res = await fetch(`${API_CONFIG.BASE_URL}/auth/send-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone: phoneNumber, challengeToken: freshToken }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message);
    }

    console.log("✅ SMS API ÇALIŞTI");

    setDriverCategory(data?.data?.driverCategory || 'yeni-kayit');

    setStep("verification");

  } catch (err) {
    console.error(err);
    setError(err instanceof Error ? err.message : 'SMS gönderilemedi');
  } finally {
    setLoading(false);
  }
};

  // Doğrulama Kodunu Kontrol Et
const verifyCode = async () => {
  setError('');
  setLoading(true);
  try {
    const res = await fetch(`${API_CONFIG.BASE_URL}/auth/verify-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone: phoneNumber,
        code: verificationCode,
        platform: getPlatform(),
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      setError(data?.message || 'Doğrulama başarısız');
      return;
    }

    // 🔥 DOĞRU YER
    const result = data.data;


    clearStoredDriverMobileUser();
    sessionStorage.removeItem('driverRegisterPhone');

    setAuthToken(result.token);
    localStorage.setItem("token", result.token);
    localStorage.setItem('currentUser', JSON.stringify(result.user));
    sessionStorage.setItem('sessionUser', JSON.stringify(result.user));
    setStoredDriverMobileUser(result.user);

    if (Capacitor.isNativePlatform()
        && !isInternalDriverUser(result.user)
        && !isCorporateExternalTransportMobileUser(result.user)
        && result.token) {
      await alwaysLocationService.configureTrackingIfAvailable({
        apiBaseUrl: API_CONFIG.BASE_URL,
        authToken: result.token,
        shipmentId: null,
      });
      await alwaysLocationService.startBackgroundTrackingIfAvailable();
    }

    console.log("isNewUser:", result.isNewUser);

    if (result.isNewUser) {
      setDriverCategory('yeni-kayit');
      sessionStorage.setItem("driverRegisterPhone", phoneNumber);
      navigate("/driver-mobile-register");
    } else if (!isAnyDriverUser(result.user)) {
      navigate("/");
    } else {
      navigate("/driver-mobile-home");
    }

  } catch (err) {
    console.error(err);
    setError('Sunucu hatası');
  } finally {
    setLoading(false);
  }
};

 
  return (
    <div className="driver-mobile-auth-page safe-area-page min-h-screen bg-gradient-to-br from-[#120700] via-[#241006] to-[#140904] flex items-center justify-center p-4 text-slate-100">
      
      <div className="max-w-md w-full">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-32 w-32 items-center justify-center rounded-3xl border border-orange-500/20 bg-[#111111] p-4 shadow-2xl">
            <img src={logo} alt="Doğuş Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="mb-2 text-3xl font-bold text-white">Yük Ortağım</h1>
          <p className="text-orange-200">Sürücü Mobil Giriş</p>
        </div>

        {/* Auth Card */}
        <div className="driver-mobile-auth-card rounded-2xl border border-orange-500/20 bg-[#111111] p-8 shadow-2xl">
          {step === 'phone' ? (
            // ADIM 1: Telefon Numarası
            <>
              <div className="mb-6">
                <h2 className="mb-2 text-2xl font-bold text-slate-100">Telefon Numaranız</h2>
                <p className="text-sm text-slate-400">
                  Doğrulama kodu göndermek için telefon numaranızı girin
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-300">
                    Telefon Numarası <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <span className="inline-flex items-center rounded-l-lg border border-r-0 border-orange-500/20 bg-[#171717] px-4 py-3 font-medium text-slate-200">
                      +90
                    </span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className="flex-1 rounded-r-lg border border-orange-500/20 bg-[#171717] px-4 py-3 text-lg text-slate-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30"
                      placeholder="5XX XXX XX XX"
                      maxLength={10}
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    ⚠️ Başında 0 olmadan 10 haneli olarak girin (örn: 5XX XXX XX XX)
                  </p>
                </div>

                {error && (
                  <div className="rounded-lg border border-rose-500/20 bg-rose-950/30 p-3">
                    <p className="text-sm text-rose-200">{error}</p>
                  </div>
                )}

                <button
                  onClick={sendVerificationCode}
                  disabled={loading || phoneNumber.length !== 10}
                  className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    loading || phoneNumber.length !== 10
                      ? 'cursor-not-allowed bg-slate-800 text-slate-500'
                      : 'bg-orange-500 text-white shadow-lg hover:bg-orange-600 hover:shadow-xl'
                   }`}
                >
                  {loading ? (
                    <span>Kod Gönderiliyor...</span>
                  ) : (
                    <>
                      <span>Doğrulama Kodu Gönder</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="border-t border-orange-500/10 pt-4">
                  <p className="text-center text-xs text-slate-500">
                    Telefon numaranıza SMS ile doğrulama kodu gönderilecektir.
                  </p>
                </div>
              </div>
            </>
          ) : (
            // ADIM 2: Doğrulama Kodu
            <>
              <div className="mb-6">
                <div className="driver-mobile-auth-icon-shell mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/15">
                   <ShieldCheck className="h-8 w-8 text-orange-400" />
                 </div>
                  <h2 className="driver-mobile-auth-title mb-2 text-center text-2xl font-bold text-slate-100">Doğrulama Kodu</h2>
                  <p className="driver-mobile-auth-subtitle text-center text-sm text-slate-400">
                      <strong>+90 {phoneNumber}</strong> numarasına gönderilen 6 haneli kodu girin
                  </p>
                  {driverCategory ? (
                    <div className="mt-3 flex justify-center">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${driverCategory === 'özmal' ? 'bg-emerald-500/15 text-emerald-300' : driverCategory === 'spot' ? 'bg-sky-500/15 text-sky-300' : 'bg-amber-500/15 text-amber-300'}`}>
                        {driverCategory === 'özmal' ? 'Özmal Sürücü' : driverCategory === 'spot' ? 'Dış Sürücü' : 'Yeni Kayıt'}
                      </span>
                    </div>
                  ) : null}
              </div>

              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 6) {
                        setVerificationCode(value);
                        setError('');
                      }
                    }}
                    className="w-full rounded-lg border-2 border-orange-500/20 bg-[#171717] px-4 py-4 text-center text-2xl font-bold tracking-widest text-slate-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30"
                      placeholder="------"
                      maxLength={6}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-rose-500/20 bg-rose-950/30 p-3">
                    <p className="text-sm text-rose-200">{error}</p>
                  </div>
                )}

                <button
                  onClick={verifyCode}
                  disabled={loading || verificationCode.length < 6}
                  className={`w-full py-3 rounded-lg font-medium transition-all ${
                    loading || verificationCode.length < 4
                      ? 'cursor-not-allowed bg-slate-800 text-slate-500'
                      : 'bg-orange-500 text-white shadow-lg hover:bg-orange-600 hover:shadow-xl'
                   }`}
                >
                  {loading ? 'Kontrol Ediliyor...' : 'Doğrula ve Devam Et'}
                </button>

                <button
                  onClick={() => {
                    setStep('phone');
                    setVerificationCode('');
                    setError('');
                  }}
                  className="w-full py-2 text-sm text-slate-400 hover:text-slate-200"
                >
                  ← Telefon numarasını değiştir
                </button>

                <div className="border-t border-orange-500/10 pt-4">
                  <button
                    onClick={sendVerificationCode}
                    className="w-full text-sm font-medium text-orange-300 hover:text-orange-200"
                  >
                    Kodu tekrar gönder
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">

          <div className="driver-mobile-auth-footer-panel mt-5 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm p-4 text-left shadow-lg">
            <p className="driver-mobile-auth-footer-caption mb-3 text-xs uppercase tracking-[0.24em] text-orange-200/80">Bilgilendirme</p>
            <div className="grid grid-cols-1 gap-3">
              <Link
                to="/about"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                    <Info className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Yükortağım Hakkında</div>
                    <div className="text-[11px] text-orange-100/80">Uygulamayı ve market linklerini inceleyin</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>

              <Link
                to="/disclosure"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Aydınlatma Metni</div>
                    <div className="text-[11px] text-orange-100/80">KVKK kapsamında bilgilendirme metni</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>

              <Link
                to="/privacy"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Gizlilik Sözleşmesi</div>
                    <div className="text-[11px] text-orange-100/80">KVKK, gizlilik ve çerez detayları</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>

              <Link
                to="/registration-agreement"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <FileSignature className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Kayıt Sözleşmesi</div>
                    <div className="text-[11px] text-orange-100/80">Dış sürücü kayıt ve onay koşulları</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>

              <Link
                to="/explicit-consent"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Açık Rıza</div>
                    <div className="text-[11px] text-orange-100/80">Arka plan konum verisi açık rızası</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>

              <Link
                to="/electronic-consent"
                className="driver-mobile-auth-footer-link group flex items-center justify-between rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-orange-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <Bell className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Ticari İleti Onayı</div>
                    <div className="text-[11px] text-orange-100/80">SMS ve WhatsApp ticari ileti onayı</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-orange-100/70 group-hover:text-white" />
              </Link>
            </div>
          </div>

          <div className="driver-mobile-auth-footer-note mt-4 text-[11px] text-orange-100/75">
            Giriş yaparak kullanım koşullarını, Aydınlatma Metni'ni, Gizlilik Sözleşmesi'ni ve Kayıt Sözleşmesi'ni kabul etmiş olursunuz.
          </div>
        </div>
      </div>
    </div>
  );
}
